import { useRef } from 'react';
import { Sparkles, Bell, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HeaderSearchBar } from '@/components/HeaderSearchBar';
import { UserProfileMenu } from '@/components/UserProfileMenu';
import { BrandBadge } from '@/cowork/BrandBadge';
import { getTokenFromStorage } from '@/utils/auth';
import { buildLegacyAppUrl } from '@/utils/legacyApp';
import beatlyLogo from 'figma:asset/78166de18001e30ac00ef2150ee31fcaaf218dc5.png';

interface TopNavBarProps {
  currentPage?: string;
  onPageChange?: (page: string) => void;
  sidebarCollapsed?: boolean;
  userName?: string;
  brandName?: string;
  activeMode?: 'chat' | 'cowork' | 'platform';
  onModeChange?: (mode: 'chat' | 'cowork' | 'platform') => void;
  /** When false, hide Platform/Cowork/Chat mode switch (staging-only feature). */
  showCowork?: boolean;
  /** Header search catalog + recents — same as {@link AppSidebar}. */
  lastCampaignPath?: string | null;
  lastCampaignDisplayName?: string | null;
}

/**
 * Centered discreet CTA to open the legacy Beatly app with the current JWT.
 * Replaces the Platform / Cowork / Chat header switcher (modes remain reachable via URL).
 */
function LegacyAppSwitchBar() {
  const { t } = useTranslation();

  const onClick = () => {
    window.location.href = buildLegacyAppUrl(getTokenFromStorage());
  };

  return (
    <div className="flex items-center gap-2 bg-secondary rounded-full pl-3 pr-1 py-1 max-w-[min(100%,28rem)]">
      <span className="text-[13px] text-muted-foreground truncate shrink min-w-0">
        {t('nav.legacySwitch.label')}
      </span>
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium text-foreground/85 hover:text-foreground hover:bg-card/70 transition-colors shrink-0"
        aria-label={t('nav.legacySwitch.action')}
      >
        {t('nav.legacySwitch.action')}
        <ExternalLink className="w-3.5 h-3.5 shrink-0" aria-hidden />
      </button>
    </div>
  );
}

/** Temporarily hide AI Assistant + Notifications; set to false to show again. */
const HIDE_TOP_NAV_AI_AND_NOTIFICATIONS = true;

export function TopNavBar({
  currentPage: _currentPage = '',
  onPageChange,
  activeMode: _activeMode = 'platform',
  onModeChange: _onModeChange,
  showCowork = false,
  lastCampaignPath = null,
  lastCampaignDisplayName = null,
}: TopNavBarProps) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={navRef}
      className="h-14 bg-white flex items-center pr-5 shrink-0 z-50 relative"
    >
      {/* Left: Logo – generous padding, natural sizing */}
      <div className="flex items-center shrink-0 pl-5 pr-4">
        <img src={beatlyLogo} alt={t('nav.logoAlt')} className="h-8 w-auto" />
      </div>

      {/* Center: legacy app switch (replaces mode switcher in header) */}
      <div className="flex-1 flex items-center justify-center min-w-0 px-2">
        <LegacyAppSwitchBar />
      </div>

      {/* Right: Search | AI | Notifications | Profile | Settings */}
      <div className="flex items-center gap-1.5 shrink-0">
        <HeaderSearchBar
          showCowork={showCowork}
          lastCampaignPath={lastCampaignPath}
          lastCampaignDisplayName={lastCampaignDisplayName}
        />

        {!HIDE_TOP_NAV_AI_AND_NOTIFICATIONS ? (
          <>
            {/* AI */}
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-[#be43ff] hover:bg-[#be43ff]/8 transition-colors"
              title={t('nav.aiAssistant')}
              aria-label={t('nav.aiAssistant')}
            >
              <Sparkles className="w-[18px] h-[18px]" />
            </button>

            {/* Notifications */}
            <button
              type="button"
              className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title={t('nav.notifications')}
              aria-label={t('nav.notifications')}
            >
              <Bell className="w-[18px] h-[18px]" />
              <span className="absolute top-1 right-1.5 w-2 h-2 bg-[#a9f600] rounded-full border-2 border-white" />
            </button>
          </>
        ) : null}

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 mx-1.5" />

        {/* Which brand the agents are acting for — beside the profile menu, not
            in place of it: that avatar is the only way to settings and logout. */}
        {showCowork && <BrandBadge />}

        <UserProfileMenu onPageChange={onPageChange} />
      </div>
    </div>
  );
}
